import { KbjCard, KbjCardMeta } from '@eika-kortbetjening/types';

import { Card, CreditCard, CreditCardAccount, CustomerRole } from '@generated-types';

import { Format } from '../app';

const creditCardStatusToKbjStatus = ({ status }: CreditCard): KbjCard['status'] => {
    if (status === 'NORMAL') {
        return 'OK';
    }
    if (status === 'BLOCKED' || status === 'TEMPORARY_BLOCKED') {
        return 'BLOCKED';
    }
    if (status === 'CLOSED') {
        return 'DELETED';
    }

    return status;
};

const isExpired = (card: CreditCard) => {
    const { year, month } = card.expires;

    return new Date(year, month) < new Date();
};

const creditCardToState = (card: CreditCard): KbjCardMeta['state'] => {
    return {
        isActive: card.status === 'NORMAL',
        isBlocked: card.status === 'BLOCKED' || card.status === 'TEMPORARY_BLOCKED',
        isExpired: isExpired(card),
        isOrdered: false,
        isOrderedToNets: false,
        isOwnedByUser: true,
        isReplaced: false,
        isSoftblocked: card.status === 'TEMPORARY_BLOCKED',
    };
};

const toKbjPanel = (card: CreditCard, getUrl: (card: CreditCard) => string): [KbjCard, KbjCardMeta] => {
    return [
        {
            embossedName: `${card.cardHolder.firstName} ${card.cardHolder.lastName}`.toUpperCase(),
            number: Format.cardNumber(card.cardNumber),
            bankRegistrationNumber: card.product.name || '123',
            orderStatus: 'ORDERED',
            status: creditCardStatusToKbjStatus(card),
            name: card.product.name || 'credit card name - mock',
            url: getUrl(card),
            imageUrl: card.imagePath,
        },
        {
            state: creditCardToState(card),
            market: 'private',
            otherOwnerName: !card.userIsCardOwner
                ? `${card.cardHolder.firstName} ${card.cardHolder.lastName}`
                : undefined,
        },
    ];
};

const matchWithAccount = (cards: CreditCard[], accounts: CreditCardAccount[]): [CreditCard, CreditCardAccount][] => {
    return cards
        .map((card) => [card, accounts.find((account) => account.cards.find((c) => c.id === card.id))])
        .filter(([, account]) => !!account) as [CreditCard, CreditCardAccount][];
};

const isOwnerOfCard = (card: CreditCard) => {
    return card.cardHolder.role === CustomerRole.ACCOUNT_OWNER;
};

const isCreditCard = (card: Card | CreditCard): card is CreditCard => {
    return 'cardNumber' in card;
};

export const CreditCardService = {
    toKbjPanel,
    matchWithAccount,
    isExpired,
    creditCardStatusToKbjStatus,
    isCreditCard,
    isOwnerOfCard,
};
